import * as React from "react";
import Navbar from "../setup/Navbar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid2 from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import axios from "axios";
import AutoComplete from "../../BuiltInComponent/AutoComplete";
import { useSelector, useDispatch } from "react-redux";
import {
  addNewItem,
  getAllItems,
  getItemDetails,
  deleteItem,
} from "../../features/StockManagement/itemsInformationSlice";
import { getItemCategoryList } from "../../features/setup/useableSlice";
import { getAllIHeads } from "../../features/GeneralManagement/itemHeadsSlice";
import { getCategorySetupData } from "../../features/GeneralManagement/itemCategorySlice";
import { getAllItemCompanies } from "../../features/GeneralManagement/itemCompanySlice";
import StickyHeadTable from "../../BuiltInComponent/TableWithPagination";
import HeadingText from "../../BuiltInComponent/HeadingTest";
import CheckBox from "../../BuiltInComponent/CheckBox";
import Loading from "../../BuiltInComponent/Loading";

const DEFAULT_VARIANT_OPTIONS = [
  "Small",
  "Medium",
  "Large",
  "Extra Large",
  "Leg",
  "Chest",
  "Quarter",
  "Half",
  "Full",
];

const normalizeVariantOption = (value) => String(value || "").trim().toLowerCase();

const buildUniqueVariantOptions = (options = []) => {
  const seen = new Set();
  const result = [];

  options.forEach((option) => {
    const cleanValue = String(option || "").trim();
    const normalized = normalizeVariantOption(cleanValue);
    if (!normalized || seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    result.push(cleanValue);
  });

  return result;
};

const buildVariantItemName = (baseName, variantValue) => {
  const cleanBase = String(baseName || "").trim();
  const cleanVariant = String(variantValue || "").trim();
  if (!cleanBase || !cleanVariant) return cleanBase;
  return `${cleanBase} (${cleanVariant})`;
};

export default function CreateUser() {
  const dispatch = useDispatch();
  const { items, isLoading } = useSelector((state) => state.itemsInf);
  const { itemCategories } = useSelector((state) => state.ru);
  const { heads } = useSelector((state) => state.ih);
  const { cateSetupData } = useSelector((state) => state.ic);
  const { itemCompanyies } = useSelector((state) => state.itmCmp);

  // Define initial state for form fields
  const initialState = {
    itemCode: null,
    cateCode: "",
    headCode: "",
    itmCmpCode: "",
    itemBarCode: "",
    itemName: "",
    isVariantItem: false,
    baseItemName: "",
    variantValue: "",
    baseUnitName: "",
    reportingUnitName: "",
    baseUnitsPerReportingUnit: "",
    inUnit: "",
    outUnit: "",
    outQuantity: "",
    minQuantity: "",
    maxQuantity: "",
    tradePrice: "",
    salesPrice: "",
    itemStatus: true,
    packBarCode: "",
    packQuantity: "",
    masterPackBarCode: "",
    masterPackQuantity: "",
    isPurchasable: true,
    file: "",
  };

  const [preview, setPreview] = React.useState(null);
  const [file, setFile] = React.useState(null);
  const [formData, setFormData] = React.useState(initialState);
  const [variantOptions, setVariantOptions] = React.useState(DEFAULT_VARIANT_OPTIONS);
  const [openSnackbar, setOpenSnackbar] = React.useState(false); // For Snackbar
  const resolvedItemName = React.useMemo(() => {
    if (!formData.isVariantItem) {
      return String(formData.itemName || "").trim();
    }

    return buildVariantItemName(formData.baseItemName, formData.variantValue);
  }, [formData.isVariantItem, formData.itemName, formData.baseItemName, formData.variantValue]);

  React.useEffect(() => {
    dispatch(getAllItems());
    dispatch(getItemCategoryList());
    dispatch(getAllIHeads());
    dispatch(getCategorySetupData());
    dispatch(getAllItemCompanies());
  }, [dispatch]);

  React.useEffect(() => {
    const loadVariantOptions = async () => {
      try {
        const response = await axios.post(`${global.SET_API_URL}singleFieldData/getAllSingleFieldData`, {
          sfScreen: "VariantSetup",
          sfHead: "Variant",
        });

        const optionsFromSetup = (response?.data || [])
          .map((row) => String(row.sfDescription || "").trim())
          .filter(Boolean);

        // Keep dropdown synced with VariantSetup records; use defaults only as fallback.
        const dbBackedOptions = buildUniqueVariantOptions(optionsFromSetup);
        if (dbBackedOptions.length > 0) {
          setVariantOptions(dbBackedOptions);
          return;
        }

        setVariantOptions(buildUniqueVariantOptions(DEFAULT_VARIANT_OPTIONS));
      } catch (error) {
        // Keep defaults when setup data is unavailable.
        setVariantOptions(buildUniqueVariantOptions(DEFAULT_VARIANT_OPTIONS));
      }
    };

    loadVariantOptions();
  }, []);

  // const validateFormData = (data) => {
  //     // Fields to ignore during validation (like booleans)
  //     const ignoreFields = ['itemStatus', 'isPurchasable', 'itemCode'];

  //     for (const key in data) {
  //         if (ignoreFields.includes(key)) continue;

  //         const value = data[key];

  //         if (value === null || value === '' || value === undefined) {
  //             return { isValid: false, message: `${key} is required.` };
  //         }
  //     }

  //     return { isValid: true };
  // };
  const onFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
      setFormData((prevState) => ({
        ...prevState,
        file: selectedFile,
      }));
    } else {
      setFile(null);
      setPreview(null);
    }
  };

  const normalizeFormDataForSubmit = (data) => {
    const numericFields = [
      'outQuantity',
      'minQuantity',
      'maxQuantity',
      'tradePrice',
      'salesPrice',
      'packQuantity',
      'masterPackQuantity',
      'baseUnitsPerReportingUnit',
    ];
    const stringFields = [
      'itemBarCode',
      'itemName',
      'baseItemName',
      'variantValue',
      'baseUnitName',
      'reportingUnitName',
      'inUnit',
      'outUnit',
      'packBarCode',
      'masterPackBarCode',
    ];

    const normalized = { ...data };

    numericFields.forEach((field) => {
      const value = normalized[field];
      if (value === null || value === '' || value === undefined) {
        normalized[field] = 0;
      } else {
        const num = Number(value);
        normalized[field] = Number.isFinite(num) ? num : 0;
      }
    });

    stringFields.forEach((field) => {
      const value = normalized[field];
      if (value === null || value === undefined) {
        normalized[field] = '';
      } else {
        normalized[field] = String(value).trim();
      }
    });

    return normalized;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate required fields
    const requiredFields = [
      { key: 'cateCode', label: 'Category Code' },
      { key: 'headCode', label: 'Head Code' },
      { key: 'itmCmpCode', label: 'Item Company Code' },
    ];

    for (const field of requiredFields) {
      const value = formData[field.key];
      if (value === null || value === undefined || String(value).trim() === '') {
        alert(`${field.label} null nahi jani chahye. Kripaya fill karein.`);
        return;
      }
    }

    const isVariantItem = Boolean(formData.isVariantItem);
    const cleanBaseName = String(formData.baseItemName || "").trim();
    const cleanVariantValue = String(formData.variantValue || "").trim();

    if (isVariantItem && (!cleanBaseName || !cleanVariantValue)) {
      return;
    }

    const payload = {
      ...formData,
      itemName: isVariantItem ? resolvedItemName : String(formData.itemName || "").trim(),
      variantType: isVariantItem ? "variant" : "single",
      baseItemName: isVariantItem ? cleanBaseName : null,
      variantValue: isVariantItem ? cleanVariantValue : null,
      baseUnitName: String(formData.baseUnitName || formData.inUnit || "").trim(),
      reportingUnitName: String(formData.reportingUnitName || formData.outUnit || "").trim(),
      baseUnitsPerReportingUnit: formData.baseUnitsPerReportingUnit || formData.outQuantity || 1,
    };

    const normalizedPayload = normalizeFormDataForSubmit(payload);

    // Helper UI field is not part of backend schema.
    delete normalizedPayload.isVariantItem;

    if (!normalizedPayload.itemName) {
      return;
    }

    let dataToSend;

    if (file) {
      dataToSend = new FormData();
      Object.keys(normalizedPayload).forEach((key) => {
        if (key === "file") {
          dataToSend.append("image", normalizedPayload.file);
        } else {
          dataToSend.append(key, normalizedPayload[key] ?? "");
        }
      });
    } else {
      dataToSend = normalizedPayload;
    }
    // const validationResult = validateFormData(formData);
    // if (!validationResult.isValid) {
    //     alert(validationResult.message);
    // } else {
    await dispatch(addNewItem(dataToSend)).then(async () => {
      await dispatch(getAllItems());
    });

    // Clear the form after submission
    const lastItemCateCode = formData.cateCode;

    await setFormData(initialState);

    await setFormData((prevState) => ({
      ...prevState,
      itemCateCode: lastItemCateCode,
      file: file,
    }));
    // Show Snackbar for success message
    await setOpenSnackbar(true);

    // }
  };

  const [errorNumOnly, setErrorNumOnly] = React.useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // sirf is field ke liye numeric enforce karna hai:
    if (name === "masterPackQuantity") {
      const cleaned = value.replace(/\D+/g, "");
      setErrorNumOnly(cleaned !== value); // agar non-digit try hua to error on
      setFormData((p) => ({ ...p, [name]: cleaned }));
      return;
    }

    // baaki fields normal
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const onChangeAutoComplete = (data) => {
    const keys = Object.keys(data);
    setFormData((prevState) => ({
      ...prevState,
      [keys[0]]: data[keys[0]],
    }));
  };

  const onEditClick = async (itemCode) => {
    const response = await dispatch(getItemDetails({ itemCode }));
    const payload = response?.payload;

    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      return;
    }

    const data = payload;
    const isVariantItem = String(data.variantType || "").toLowerCase() === "variant";
    const baseItemName = isVariantItem ? String(data.baseItemName || "").trim() : "";
    const variantValue = isVariantItem ? String(data.variantValue || "").trim() : "";

    if (
      isVariantItem &&
      variantValue &&
      !variantOptions.some(
        (option) => normalizeVariantOption(option) === normalizeVariantOption(variantValue)
      )
    ) {
      setVariantOptions((prevOptions) => buildUniqueVariantOptions([...prevOptions, variantValue]));
    }

    setFormData((prevState) => ({
      ...prevState,
      itemName: data.itemDesc || "",
      itemCode: data.itemCode || null,
      cateCode: data.categoryCode || "",
      headCode: data.headCode || "",
      itmCmpCode: data.itmCmpCode || "",
      itemBarCode: data.barCode || "",
      isVariantItem,
      baseItemName,
      variantValue,
      baseUnitName: data.baseUnitName || data.inUnit || "",
      reportingUnitName: data.reportingUnitName || data.outUnit || "",
      baseUnitsPerReportingUnit: data.baseUnitsPerReportingUnit || data.outQuantity || "",
      tradePrice: data.tradePrice || "",
      salesPrice: data.salesPrice || "",
      inUnit: data.inUnit || "",
      outUnit: data.outUnit || "",
      outQuantity: data.outQuantity || "",
      minQuantity: data.minQuantity || "",
      maxQuantity: data.maxQuantity || "",
      packBarCode: data.packBarCode || "",
      packQuantity: data.packQuantity || "",
      masterPackBarCode: data.masterPackBarCode || "",
      masterPackQuantity: data.masterPackQuanttiy || "",
      itemStatus: typeof data.itemStatus === "undefined" ? true : data.itemStatus,
      isPurchasable: typeof data.isPurchasable === "undefined" ? true : data.isPurchasable,
      path: data.itemImage || null,
    }));

    setPreview(
      data.itemImage ? `${global.SET_API_URL}uploads/${data.itemImage}` : null
    );
  };

  const onDeleteClick = async (itemCode) => {
    await dispatch(deleteItem({ itemCode: itemCode }));

    await dispatch(getAllItems());
  };

  const tableColumns = [
    {
      fieldName: "itemCode",
      cName: "Item Code",
      textAlign: "left",
      width: 80,
      status: "en",
      dType: "string",
    },
    {
      fieldName: "barCode",
      cName: "Bar Code",
      textAlign: "left",
      width: 80,
      status: "en",
      dType: "string",
    },
    {
      fieldName: "cateDescription",
      cName: "Category",
      textAlign: "left",
      width: 80,
      status: "en",
      dType: "string",
    },
    {
      fieldName: "headDescription",
      cName: "Head",
      textAlign: "left",
      width: 80,
      status: "en",
      dType: "string",
    },
    {
      fieldName: "itmCmpDescription",
      cName: "Company",
      textAlign: "left",
      width: 80,
      status: "en",
      dType: "string",
    },
    {
      fieldName: "itemDesc",
      cName: "Item Name",
      textAlign: "left",
      width: 80,
      status: "en",
      dType: "string",
    },
    {
      fieldName: "Edit",
      cName: "Edit",
      textAlign: "center",
      width: 80,
      status: "en",
      dType: "string",
    },
    {
      fieldName: "Delete",
      cName: "Delete",
      textAlign: "center",
      width: 80,
      status: "en",
      dType: "string",
    },
    {
      fieldName: "Delete",
      cName: "Delete",
      textAlign: "right",
      width: 80,
      status: "dis",
      dType: "string",
    },
  ];

  const onChangeCheckBox = (event) => {
    const { name, checked } = event.target;
    console.log(event.target);

    if (name === "isVariantItem" && !checked) {
      setFormData((prevState) => ({
        ...prevState,
        isVariantItem: checked,
        baseItemName: "",
        variantValue: "",
      }));
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  return (
    <>
      <Navbar />
      <Container>
        <HeadingText text="Items Information" />
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Typography sx={{ fontWeight: "bolder", color: "#636362" }}>
                Group Details
              </Typography>
            </Grid2>
            <Grid2 size={3}>
              {cateSetupData && cateSetupData.length > 0 ? (
                cateSetupData[0].category === 1 ? (
                  <AutoComplete
                    label="Select Category"
                    listData={itemCategories && itemCategories}
                    value={itemCategories.find(
                      (c) => c.cateCode === formData.cateCode
                    )}
                    onChange={onChangeAutoComplete}
                  />
                ) : (
                  <></>
                )
              ) : (
                <></>
              )}
            </Grid2>
            <Grid2 size={3}>
              {cateSetupData && cateSetupData.length > 0 ? (
                cateSetupData[0].category === 1 ? (
                  <AutoComplete
                    label="Select Heads"
                    listData={heads && heads}
                    value={heads.find((c) => c.headCode === formData.headCode)}
                    onChange={onChangeAutoComplete}
                  />
                ) : (
                  <></>
                )
              ) : (
                <></>
              )}
            </Grid2>
            <Grid2 size={3}>
              <AutoComplete
                label="Select Companies"
                listData={itemCompanyies && itemCompanyies}
                value={itemCompanyies.find(
                  (c) => c.itmCmpCode === formData.itmCmpCode
                )}
                onChange={onChangeAutoComplete}
              />
            </Grid2>
            <Grid2 size={6}></Grid2>
            <Grid2 size={12}>
              <Typography sx={{ fontWeight: "bolder", color: "#636362" }}>
                Basic Details
              </Typography>
            </Grid2>
            <Grid2 size={3}>
              <TextField
                value={formData.itemBarCode}
                onChange={handleInputChange}
                name="itemBarCode"
                required
                fullWidth
                id="itemBarCode"
                label="Bar Code"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={2}>
              <CheckBox
                label="Variant Item"
                checked={formData.isVariantItem}
                onChange={onChangeCheckBox}
                name="isVariantItem"
              />
            </Grid2>
            <Grid2 size={5}>
              <TextField
                value={formData.isVariantItem ? resolvedItemName : formData.itemName}
                onChange={handleInputChange}
                name="itemName"
                required
                fullWidth
                id="itemName"
                label="Item Name"
                autoFocus
                size="small"
                disabled={formData.isVariantItem}
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.tradePrice}
                onChange={handleInputChange}
                name="tradePrice"
                required
                fullWidth
                id="tradePrice"
                label="Trade Price"
                autoFocus
                size="small"
              />
            </Grid2>
            {formData.isVariantItem && (
              <>
                <Grid2 size={4}>
                  <TextField
                    value={formData.baseItemName}
                    onChange={handleInputChange}
                    name="baseItemName"
                    required
                    fullWidth
                    id="baseItemName"
                    label="Base Item Name"
                    size="small"
                  />
                </Grid2>
                <Grid2 size={3}>
                  <TextField
                    value={formData.variantValue}
                    onChange={handleInputChange}
                    name="variantValue"
                    required
                    fullWidth
                    id="variantValue"
                    label="Variant"
                    size="small"
                    select
                  >
                    {variantOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>
                <Grid2 size={5}>
                  <TextField
                    value={resolvedItemName}
                    fullWidth
                    id="finalItemName"
                    label="Final Item Name"
                    size="small"
                    disabled
                  />
                </Grid2>
              </>
            )}
            <Grid2 size={2}>
              <TextField
                value={formData.salesPrice}
                onChange={handleInputChange}
                name="salesPrice"
                required
                fullWidth
                id="salesPrice"
                label="Sales Price"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={12}>
              {preview ? (
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    width: 100,
                    height: 100,
                    marginTop: 10,
                    objectFit: "cover",
                  }}
                />
              ) : formData.imageUrl ? (
                <img
                  src={formData.imageUrl}
                  alt="Current"
                  style={{
                    width: 100,
                    height: 100,
                    marginTop: 10,
                    objectFit: "cover",
                  }}
                />
              ) : null}
            </Grid2>
            <Grid2 size={12}>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="raised-button-file"
                multiple
                type="file"
                onChange={onFileChange}
              />
              <label htmlFor="raised-button-file">
                <Button variant="contained" component="span" fullWdth>
                  Upload Image
                </Button>
              </label>
            </Grid2>
            <Grid2 size={12}>
              <Typography sx={{ fontWeight: "bolder", color: "#636362" }}>
                Transactio Details
              </Typography>
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.inUnit}
                onChange={handleInputChange}
                name="inUnit"
                required
                fullWidth
                id="inUnit"
                label="In Unit"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.outUnit}
                onChange={handleInputChange}
                name="outUnit"
                required
                fullWidth
                id="outUnit"
                label="Out Unit"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.outQuantity}
                onChange={handleInputChange}
                name="outQuantity"
                required
                fullWidth
                id="outQuantity"
                label="Out Unit Quantity"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.baseUnitName}
                onChange={handleInputChange}
                name="baseUnitName"
                fullWidth
                id="baseUnitName"
                label="Base Unit"
                size="small"
                helperText="Example: Seekh"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.reportingUnitName}
                onChange={handleInputChange}
                name="reportingUnitName"
                fullWidth
                id="reportingUnitName"
                label="Reporting Unit"
                size="small"
                helperText="Example: Plate"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.baseUnitsPerReportingUnit}
                onChange={handleInputChange}
                name="baseUnitsPerReportingUnit"
                fullWidth
                id="baseUnitsPerReportingUnit"
                label="Base Units / Reporting"
                size="small"
                helperText="Example: 2 seekh = 1 plate"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.minQuantity}
                onChange={handleInputChange}
                name="minQuantity"
                required
                fullWidth
                id="minQuantity"
                label="Minimum Quantity"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.maxQuantity}
                onChange={handleInputChange}
                name="maxQuantity"
                required
                fullWidth
                id="maxQuantity"
                label="Max Quantity"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={12}>
              <Typography sx={{ fontWeight: "bolder", color: "#636362" }}>
                Other BarCode Details
              </Typography>
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.packBarCode}
                onChange={handleInputChange}
                name="packBarCode"
                required
                fullWidth
                id="packBarCode"
                label="Pack Bar Code"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.packQuantity}
                onChange={handleInputChange}
                name="packQuantity"
                required
                fullWidth
                id="packQuantity"
                label="Pack Quantity"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData.masterPackBarCode}
                onChange={handleInputChange}
                name="masterPackBarCode"
                required
                fullWidth
                id="masterPackBarCode"
                label="Master Pack Bar Code"
                autoFocus
                size="small"
              />
            </Grid2>
            <Grid2 size={2}>
              <TextField
                value={formData?.masterPackQuantity ?? ""}
                onChange={handleInputChange}
                name="masterPackQuantity"
                required
                fullWidth
                id="masterPackQuantity"
                label="Master Bar Code Quantity"
                autoFocus
                size="small"
                type="text"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }} // mobile numeric keyboard
                onKeyDown={(e) => {
                  const allowed = [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                    "Home",
                    "End",
                  ];
                  if (allowed.includes(e.key)) return;
                  if (!/^\d$/.test(e.key)) e.preventDefault(); // typing guard
                }}
                onPaste={(e) => {
                  // paste guard
                  const text = (
                    e.clipboardData || window.clipboardData
                  ).getData("text");
                  if (!/^\d+$/.test(text)) e.preventDefault();
                }}
                error={errorNumOnly}
                helperText={errorNumOnly ? "Only numbers allowed" : ""}
              />
            </Grid2>
            <Grid2 size={12}>
              <Typography sx={{ fontWeight: "bolder", color: "#636362" }}>
                Controls
              </Typography>
            </Grid2>
            <Grid2 size={2}>
              <CheckBox
                label="Is Purchasable"
                checked={formData.isPurchasable}
                onChange={onChangeCheckBox}
                name="isPurchasable"
              />
            </Grid2>
            <Grid2 size={2}>
              <CheckBox
                label="Item Status"
                checked={formData.itemStatus}
                onChange={onChangeCheckBox}
                name="itemStatus"
              />
            </Grid2>
            <Grid2 size={2} style={{ marginTop: -22 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                ADD ITEM
              </Button>
            </Grid2>
          </Grid2>
        </Box>

        {/* Snackbar for alert */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }} // Set the position to top-right
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            sx={{ width: "100%", marginTop: 100 }}
          >
            Item added successfully!
          </Alert>
        </Snackbar>
        {/* Loading component */}
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <Typography
              style={{
                textAlign: "center",
                fontSize: 25,
                margin: 10,
                borderBottom: "5px double black",
              }}
            >
              {<HeadingText text="Items List" />}
            </Typography>
            <StickyHeadTable
              tableColumns={tableColumns}
              tableData={items && items}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
            />
          </>
        )}
      </Container>
    </>
  );
}
